import { CalendarIcon, ClockIcon, DurationIcon, GlobeIcon, ShirtIcon } from "@xola/icons";
import { DateFormat, formatDate, getTimezoneAbbreviation, getTimezoneName, isValidTimeZoneId } from "common";
import dayjs from "dayjs";
import pluralize from "pluralize";
import React, { ReactNode } from "react";
import { Event, Experience } from "types";
import { getStart } from "../../../models/Event";
import { NoTranslate } from "../NoTranslate";
import { BannerDataProps } from "./BannerDataProps";
import { formatDuration } from "@/helpers/durationHelper";
import { getSeller } from "@/utils/getSeller";

type BannerArrivalProps = { event?: Event; experience?: Experience } & Pick<
    BannerDataProps,
    | "isUnScheduled"
    | "expireDate"
    | "arrivalDate"
    | "arrivalTime"
    | "isVirtual"
    | "arrivalTimezoneName"
    | "variantCount"
    | "merchandiseVariantName"
>;

export const BannerArrival = ({
    isUnScheduled,
    expireDate,
    arrivalDate,
    arrivalTime,
    isVirtual = false,
    arrivalTimezoneName,
    event,
    experience,
    variantCount,
    merchandiseVariantName,
}: BannerArrivalProps) => {
    const eventDuration = experience?.eventDuration ?? experience?.duration ?? 0;
    const isMultiDayEvent = !!(eventDuration && eventDuration > 1440);
    const showTimezoneName =
        (isVirtual || event?.seller.id !== getSeller().id) && isValidTimeZoneId(arrivalTimezoneName);
    return (
        <div className="flex space-x-2">
            {variantCount && variantCount > 0 && (
                <span className="flex items-center space-x-1">
                    <ShirtIcon />
                    <NoTranslate data-testid="banner-variant-count">
                        {merchandiseVariantName && `${merchandiseVariantName} • `}
                        {variantCount} {pluralize("Item", variantCount)}
                    </NoTranslate>
                </span>
            )}
            <span className="flex items-center space-x-1">
                {isUnScheduled ? (
                    expireDate ? (
                        <DateDisplay
                            dataTestId="banner-expire-date"
                            dateText={
                                <>
                                    Valid until <var data-var="expireDate">{expireDate}</var>
                                </>
                            }
                        />
                    ) : null
                ) : isMultiDayEvent ? (
                    <MultiDayDateDisplay experience={experience} event={event} dataTestId="banner-arrival-date" />
                ) : arrivalDate ? (
                    <DateDisplay dateText={<NoTranslate>{arrivalDate}</NoTranslate>} dataTestId="banner-arrival-date" />
                ) : null}
            </span>
            {arrivalTime ? (
                <span className="flex items-center space-x-1">
                    <ClockIcon />
                    <NoTranslate data-testid="banner-arrival-time">{arrivalTime}</NoTranslate>
                </span>
            ) : null}
            {showTimezoneName ? (
                <span className="flex items-center space-x-1">
                    <TimezoneDisplay timezoneId={arrivalTimezoneName} dataTestId="banner-timezone" />
                </span>
            ) : null}
            {!isUnScheduled && experience?.eventDuration ? (
                <span className="flex items-center space-x-1">
                    <DurationIcon />
                    <NoTranslate data-testid="banner-duration">{formatDuration(experience.eventDuration)}</NoTranslate>
                </span>
            ) : null}
        </div>
    );
};

const DateDisplay = ({ dateText, dataTestId }: { dateText: ReactNode; dataTestId: string }) => {
    return (
        <>
            <CalendarIcon />
            <NoTranslate data-testid={dataTestId}>{dateText}</NoTranslate>
        </>
    );
};

const MultiDayDateDisplay = ({
    event,
    experience,
    dataTestId,
}: {
    event?: Event;
    experience?: Experience;
    dataTestId: string;
}) => {
    if (!event || !experience) {
        return null;
    }

    const eventDuration = experience?.eventDuration ?? experience?.duration ?? 0;
    const startDate = formatDate(getStart(event), DateFormat.DATE_LONG);
    // subtracting 1 sec on the event duration to ensure end date is not spilled to next day.
    const endDate = formatDate(
        getStart(event).add(eventDuration, "minutes").subtract(1, "seconds"),
        DateFormat.DATE_LONG,
    );
    const dateText = (
        <NoTranslate>
            {startDate} - {endDate}
        </NoTranslate>
    );

    return <DateDisplay dateText={dateText} dataTestId={dataTestId} />;
};

const TimezoneDisplay = ({ timezoneId, dataTestId }: { timezoneId: string; dataTestId: string }) => {
    const date = dayjs().tz(timezoneId);
    const timezoneAbberivation = getTimezoneAbbreviation(getTimezoneName(date));
    return (
        <>
            <GlobeIcon />
            <NoTranslate data-testid={dataTestId}>{`${timezoneAbberivation} (${timezoneId})`}</NoTranslate>
        </>
    );
};
